import _defineProperty from "/drone/src/node_modules/@babel/runtime/helpers/esm/defineProperty";
import _objectWithoutProperties from "/drone/src/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["columns", "data", "tableProps", "enableHeader", "enableRowSelection", "stickyHead", "actions", "enableActionSheet", "enableVirtualization", "averageItemHeight", "parentContainerRef"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import { useMemo, useRef } from 'react';
import { useDataTableRowSorting } from './useDataTableRowSorting';
import { useDataTableRowSelection } from './useDataTableRowSelection';
import { useDataTableStickyColumns } from './useDataTableStickyColumns';
import { useDataTableExpandableSections } from './useDataTableExpandableSections';
import { DATA_TABLE_EMPTY_COLUMNS, DATA_TABLE_EMPTY_DATA, DATA_TABLE_EXPANDABLE_SECTION_OFFSET_LEFT_BASE, DATA_TABLE_EXPANDABLE_SECTION_OFFSET_LEFT_ROW_SELECTION } from '../constants';
import { useDataTablePageNavigation } from './useDataTablePageNavigation';
import { TableCell, TableControllerCell, TableHeadCell } from '../../Table';
import { useRootRef, useVirtualization } from '../../../system';
import { formatActionSheetLabel } from '../utils/formatActionSheetLabel';
export var useDataTable = function useDataTable(_ref, ref) {
  var columns = _ref.columns,
      data = _ref.data,
      tableProps = _ref.tableProps,
      enableHeader = _ref.enableHeader,
      enableRowSelection = _ref.enableRowSelection,
      stickyHead = _ref.stickyHead,
      actions = _ref.actions,
      enableActionSheet = _ref.enableActionSheet,
      enableVirtualization = _ref.enableVirtualization,
      averageItemHeight = _ref.averageItemHeight,
      parentContainerRef = _ref.parentContainerRef,
      restProps = _objectWithoutProperties(_ref, _excluded);

  var innerRef = useRootRef(ref);
  var tableBodyRef = useRef(null);
  var columnsSafe = columns || DATA_TABLE_EMPTY_COLUMNS;
  var dataSafe = data || DATA_TABLE_EMPTY_DATA;
  var rowSorting = useDataTableRowSorting(columnsSafe, restProps);
  var rowSelection = useDataTableRowSelection(dataSafe, _objectSpread({
    enableRowSelection: enableRowSelection
  }, rowSorting.restProps));
  var actionSheetLabel = '';
  var stickyColumns = useDataTableStickyColumns(columnsSafe, _objectSpread({
    enableRowSelection: enableRowSelection
  }, rowSelection.restProps));
  var expandableSections = useDataTableExpandableSections(columnsSafe, stickyColumns.restProps);
  var pageNavigation = useDataTablePageNavigation(expandableSections.restProps);

  var _useVirtualization = useVirtualization(!!enableVirtualization, tableBodyRef, dataSafe, {
    averageItemHeight: averageItemHeight,
    itemSelector: '.DataTable-Row',
    parentContainerRef: parentContainerRef
  }),
      virtualData = _useVirtualization.data,
      virtualBottomOffset = _useVirtualization.bottomOffset,
      virtualTopOffset = _useVirtualization.topOffset,
      virtualRangeStart = _useVirtualization.rangeStart;

  useMemo(function () {
    actionSheetLabel = formatActionSheetLabel(rowSelection.selectedRowIdsActual);
    return actionSheetLabel;
  }, [rowSelection]);
  var columnCount = columnsSafe.length + (enableRowSelection ? 1 : 0);
  var enableFooter = pageNavigation.result.enablePageNavigation;
  var _expandableSections$r = expandableSections.result,
      enableExpandableSections = _expandableSections$r.enableExpandableSections,
      expandableSectionControllers = _expandableSections$r.expandableSectionControllers,
      onExpansionSectionControllerTriggerClick = _expandableSections$r.onExpansionSectionControllerTriggerClick,
      expandedSectionIds = _expandableSections$r.expandedSectionIds;
  var _rowSorting$result = rowSorting.result,
      rowSortOrder = _rowSorting$result.rowSortOrder,
      onHeadCellClick = _rowSorting$result.onHeadCellClick;
  return _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({
    columns: columnsSafe,
    data: virtualData,
    actions: actions,
    actionSheetProps: {
      label: actionSheetLabel
    },
    isActionSheetVisible: enableActionSheet && actionSheetLabel
  }, rowSorting.result), rowSelection.result), expandableSections.result), pageNavigation.result), {}, {
    selectedRowIds: rowSelection.selectedRowIds,
    unselectRows: rowSelection.unselectRows,
    rootProps: _objectSpread(_objectSpread({}, pageNavigation.restProps), {}, {
      ref: innerRef
    }),
    tableProps: _objectSpread(_objectSpread({
      wide: true,
      stickyHead: stickyHead
    }, tableProps), {}, {
      stickyColumns: stickyColumns.result.stickyColumns,
      horizontalScroll: stickyColumns.result.stickyColumns.length > 0
    }),
    tableBodyProps: {
      ref: tableBodyRef
    },
    getRowProps: function getRowProps(item) {
      return {
        'data-rowid': item.id
      };
    },
    getHeadCellProps: function getHeadCellProps(column) {
      var isController = enableExpandableSections && expandableSectionControllers[column.id];
      var paddingLeft = isController ? DATA_TABLE_EXPANDABLE_SECTION_OFFSET_LEFT_BASE : undefined;
      var id = column.id,
          sortType = column.sortType,
          cellProps = column.cellProps,
          headCellProps = column.headCellProps;
      return _objectSpread(_objectSpread(_objectSpread({}, cellProps), headCellProps), {}, {
        paddingLeft: paddingLeft,
        columnId: id,
        'data-columnid': id,
        sortable: true,
        rowSortType: sortType,
        rowSortOrder: rowSortOrder,
        onClick: onHeadCellClick
      });
    },
    getCellProps: function getCellProps(column, item) {
      var isSectionExpanded = enableExpandableSections && expandedSectionIds.includes(item.id.toString());
      return _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, column.cellProps), column.dataCellProps), column.getDataCellProps ? column.getDataCellProps(item[column.id], item, column) : {}), {}, {
        'data-rowid': item.id,
        'data-columnid': column.id,
        rowId: item.id,
        onTriggerClick: onExpansionSectionControllerTriggerClick,
        expanded: isSectionExpanded
      });
    },
    getExpandableSectionProps: function getExpandableSectionProps(item) {
      var isSectionExpanded = enableExpandableSections && expandedSectionIds.includes(item.id.toString());
      return {
        colSpan: columnCount,
        offsetLeft: DATA_TABLE_EXPANDABLE_SECTION_OFFSET_LEFT_BASE + (enableRowSelection ? DATA_TABLE_EXPANDABLE_SECTION_OFFSET_LEFT_ROW_SELECTION : 0),
        expanded: isSectionExpanded,
        'data-expandablesectionid': item.id
      };
    },
    getCellTag: function getCellTag(column) {
      var isController = enableExpandableSections && expandableSectionControllers[column.id];
      return isController ? TableControllerCell : TableCell;
    },
    getHeadCellTag: function getHeadCellTag(column) {
      return column.sortable ? TableHeadCell : TableCell;
    },
    enableHeader: enableHeader !== false,
    enableFooter: enableFooter,
    enableVirtualization: enableVirtualization,
    getVirtualTopSpaceProps: function getVirtualTopSpaceProps() {
      return {
        height: virtualTopOffset
      };
    },
    getVirtualBottomSpaceProps: function getVirtualBottomSpaceProps() {
      return {
        height: virtualBottomOffset
      };
    },
    getRowNumber: function getRowNumber(index) {
      return index + virtualRangeStart;
    },
    initialDataLength: dataSafe.length
  });
};